import { memo, ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import classes from 'classnames';
import { LoadableComponentMethods } from '@loadable/component';
import Heading from '@/components/Heading/Heading';
import Link from '@/components/Link/Link';
import Paragraph from '@/components/Paragraph/Paragraph';
import styles from './MobileMenu.module.scss';

import CloseIcon from '@/assets/icons/close.svg?react';
import LogoutIcon from '@/assets/icons/logout.svg?react';
import useEventCollector from '@/hooks/context/EventCollector/useEventCollector';
import LinkEvent from '@/hooks/context/EventCollector/events/LinkEvent';
import ButtonEvent from '@/hooks/context/EventCollector/events/ButtonEvent';

export type SubMenuItem = {
  disabled?: boolean;
  url?: string;
  title: string;
  dataTest?: string;
};

export type MenuItem = {
  subMenu?: Array<SubMenuItem>;
  title: string;
  url?: string;
  additionalActiveState?: string[];
  icon?: ReactNode;
  disabled?: boolean;
  dataTest: string;
  preload?: LoadableComponentMethods<unknown>['preload'];
};

export type Props = {
  className?: string;
  clientName?: string;
  clientContract?: string;
  active: string;
  onSelect?: (url: string) => void;
  menuItems: MenuItem[];
  onClose?: () => void;
  onLogout?: () => void;
  pending?: boolean;
};

const MobileMenuMemo = memo(function MobileMenu({
  className,
  active,
  clientName,
  clientContract,
  onSelect,
  menuItems,
  onClose,
  onLogout,
  pending,
}: Props) {
  const { t } = useTranslation('menu');

  useEffect(() => {
    menuItems.forEach((item) => item.preload?.());
  }, [menuItems]);

  const [isSubMenuOpen, setSubMenuOpen] = useState(false);
  const { track } = useEventCollector();

  const handleToggleSubMenu = useCallback((item: string) => {
    setSubMenuOpen((prev) => {
      track(
        new ButtonEvent({
          description: 'submenu toggle',
          content: item,
        }),
      );

      return !prev;
    });
  }, []);

  // Prevent main body from scrolling when menu is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  });

  const handleClick = (url: string) => {
    if (onSelect) {
      onSelect(url);
      track(
        new LinkEvent({
          description: 'menu link',
          content: url,
        }),
      );
    }
  };

  return (
    <div className={classes(styles.container, className)}>
      {onClose && (
        <div className={classes(styles.close)}>
          <CloseIcon onClick={onClose} className={styles['close-icon']} />
        </div>
      )}
      <div className={styles['scroll-box']}>
        <Heading className={styles.heading}>{clientName}</Heading>
        {clientContract && (
          <Paragraph type="small" className={styles.contract}>
            {t('contract', { clientContract })}
          </Paragraph>
        )}
        <div className={styles.menu}>
          {menuItems.map((menu, index) => {
            const onMenuClick = menu.disabled
              ? undefined
              : () => handleToggleSubMenu(menu.title);
            if (menu.subMenu) {
              return (
                <div
                  className={classes([
                    styles.item,
                    styles.fleOne,
                    menu.disabled && styles.disabled,
                  ])}
                  onClick={onMenuClick}
                  onKeyDown={onMenuClick}
                  role="button"
                  tabIndex={0}
                  key={`${menu.url}-${index}`}
                >
                  <div className={styles.flex}>
                    <div
                      className={classes(
                        styles.icon,
                        (active === menu.url ||
                          menu.additionalActiveState?.includes(active)) &&
                          styles['selected-icon'],
                      )}
                    >
                      {menu.icon}
                    </div>
                    <div className={classes(styles.title)}>{menu.title}</div>
                  </div>
                  {isSubMenuOpen && (
                    <div>
                      <div className={styles.subMenuCard}>
                        {menu.subMenu?.map((subMenuItem, i) => {
                          const linkToRedirect = subMenuItem.url || '';
                          if (subMenuItem.url) {
                            return (
                              <RouterLink
                                data-test={`sidebar-${subMenuItem.dataTest}`}
                                className={classes([
                                  styles.item,
                                  styles.link,
                                  subMenuItem.disabled && styles.disabled,
                                ])}
                                onClick={() => handleClick(linkToRedirect)}
                                to={linkToRedirect}
                                key={`${linkToRedirect}-${i}`}
                              >
                                <div className={classes(styles.title)}>
                                  {subMenuItem.title}
                                </div>
                              </RouterLink>
                            );
                          }
                          return (
                            <div
                              className={classes(styles.title)}
                              key={`${subMenuItem.url}-${i}`}
                            >
                              {subMenuItem.title}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            if (menu && menu.url) {
              const linkToRedirect = menu.url;
              return (
                <RouterLink
                  data-test={`mobile-menu-${menu.dataTest}`}
                  className={classes(
                    styles.item,
                    (active === menu.url ||
                      menu.additionalActiveState?.includes(active)) &&
                      styles.selected,
                    menu.disabled && styles.disabled,
                  )}
                  to={menu.url}
                  onClick={() => handleClick(linkToRedirect)}
                  key={`${menu.url}-${index}`}
                >
                  <div
                    className={classes(
                      styles.icon,
                      (active === menu.url ||
                        menu.additionalActiveState?.includes(active)) &&
                        styles['selected-icon'],
                    )}
                  >
                    {menu.icon}
                  </div>
                  <p className={styles.title}>{menu.title}</p>
                </RouterLink>
              );
            }
            return (
              <>
                <div
                  className={classes(
                    styles.icon,
                    (active === menu.url ||
                      menu.additionalActiveState?.includes(active)) &&
                      styles['selected-icon'],
                  )}
                >
                  {menu.icon}
                </div>
                <p className={styles.title}>{menu.title}</p>
              </>
            );
          })}
        </div>
        <div className={styles['logout-container']}>
          <Link disabled={pending} onClick={onLogout} className={styles.logout}>
            <div>{t('logout')}</div>
            <LogoutIcon />
          </Link>
        </div>
      </div>
    </div>
  );
});

export default MobileMenuMemo;
